import {useState} from "react";
import {useTranslation} from "react-i18next";
import {Box, Button, CircularProgress, TextField, Typography} from "@mui/material";
import AppModalBox from "./AppModalBox";
import {createStream} from "../api/api";

interface Props {
    onClose: () => void;
    onSuccess: () => void;
    onError: (error: string) => void;
}

const StreamForm = ({onClose, onSuccess, onError}: Props) => {
    const {t} = useTranslation();

    const [name, setName] = useState("");
    const [startingDate, setStartingDate] = useState("");
    const [endingDate, setEndingDate] = useState<string>("");

    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            onError(t("streamslist.errors.nameRequired"));
            return;
        }

        if (!startingDate) {
            onError(t("streamslist.errors.startingDateRequired"));
            return;
        }

        setLoading(true);

        try {
            await createStream({
                name: name.trim(),
                startingDate,
                endingDate: endingDate ? endingDate : null,
            });

            onSuccess();
            onClose();
        } catch (error: any) {
            onError(error?.message || t("streamslist.errors.createFailed"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AppModalBox sx={{margin: "auto", display: "flex", flexDirection: "column", gap: 2}}>
            <Typography variant="h6" sx={{mb: 2}}>
                {t("streamslist.addStream")}
            </Typography>

            <form onSubmit={handleSubmit}>
                <TextField
                    label={t("streamslist.name")}
                    fullWidth
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={loading}
                    sx={{mb: 2}}
                />

                <TextField
                    label={t("streamslist.startingDate")}
                    type="date"
                    fullWidth
                    required
                    value={startingDate}
                    onChange={(e) => setStartingDate(e.target.value)}
                    disabled={loading}
                    InputLabelProps={{shrink: true}}
                    sx={{mb: 2}}
                />

                <TextField
                    label={t("streamslist.endingDateOptional")}
                    type="date"
                    fullWidth
                    value={endingDate}
                    onChange={(e) => setEndingDate(e.target.value)}
                    disabled={loading}
                    InputLabelProps={{shrink: true}}
                    sx={{mb: 2}}
                />

                <Box sx={{display: "flex", gap: 1}}>
                    <Button onClick={onClose} disabled={loading} fullWidth variant="outlined">
                        {t("common.cancel")}
                    </Button>

                    <Button type="submit" variant="contained" disabled={loading} fullWidth>
                        {loading ? <CircularProgress size={22}/> : t("common.create")}
                    </Button>
                </Box>
            </form>
        </AppModalBox>
    );
};

export default StreamForm;